'use client'

import { signIn } from 'next-auth/react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import { resendOTP } from '@/app/actions/patientLogin'
import Button from '@/components/ui/CustomButton'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { toast } from '@/components/ui/use-toast'

import { revalidatePath } from '../actions'
import useAuthStore from '../store'

const OTPInput = ({ otp, setOtp }: { otp: string; setOtp: (value: string) => void }) => (
  <InputOTP value={otp} onChange={setOtp} maxLength={6} className='w-full'>
    <InputOTPGroup className='flex w-full gap-2'>
      {[...Array(6)].map((_, index) => (
        <InputOTPSlot
          key={index}
          index={index}
          className={`
            h-10 w-10 rounded-lg bg-[#F9FAFC] text-xl

            md:h-12 md:w-12

            sm:h-12 sm:w-12 sm:text-2xl
          `}
        />
      ))}
    </InputOTPGroup>
  </InputOTP>
)

const maskMobileNumber = (phoneNumber: string) => {
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber)

  if (parsedPhoneNumber) {
    const nationalNumber = parsedPhoneNumber.nationalNumber
    const lastFourDigits = nationalNumber.slice(-4)

    return `+${parsedPhoneNumber.countryCallingCode}${'X'.repeat(Math.max(nationalNumber.length - 4, 0))}${lastFourDigits}`
  }

  const lastFourDigits = phoneNumber.slice(-4)

  return lastFourDigits ? `${'X'.repeat(Math.max(phoneNumber.length - 4, 0))}${lastFourDigits}` : ''
}

const ResendOTPButton = ({ user }: { user: number | undefined }) => {
  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const setOtp = useAuthStore((state) => state.setOtp)

  // const handleResend = async () => {
  //   setIsResendDisabled(true)
  //   setCountdown(30)
  //   const timer = setInterval(() => {
  //     setCountdown((prev) => {
  //       if (prev <= 1) clearInterval(timer)
  //       return prev - 1
  //     })
  //   }, 1000)
  //   setTimeout(() => setIsResendDisabled(false), 30000)

  //   if (user) {
  //     try {
  //       const res = await resendOTP(user)
  //       if (res?.data?.otp) {
  //         setOtp(res.data.otp)
  //       }
  //     } catch (error) {
  //       console.error('Failed to resend OTP:', error)
  //     }
  //   }
  // }

  const handleResend = async () => {
    if (!user) return

    setIsResendDisabled(true)
    setCountdown(30)
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          setIsResendDisabled(false)
        }
        return prev - 1
      })
    }, 1000)

    try {
      const res = await resendOTP(user)
      
      if (res?.error) {
        // Handle error response
        toast({
          title: 'Error',
          description: res.error,
          variant: 'error',
        })
      } else if (res?.success) {
        // Handle success response
        if (res.data?.otp) {
          setOtp(res.data.otp)
        }
        toast({
          title: 'Success',
          description: 'OTP Resent Successfully',
          variant: 'success',
        })
      }
    } catch (error) {
      console.error('Failed to resend OTP:', error)
      toast({
        title: 'Error',
        description: 'Failed to resend OTP. Please try again.',
        variant: 'error',
      })
    }
  }

  return (
    <>
      <button
        disabled={isResendDisabled || !user}
        onClick={handleResend}
        className={`
          text-link cursor-pointer text-center text-sm lg:text-base font-normal text-opacity-80

          hover:text-opacity-100 hover:underline

          ${isResendDisabled || !user ? 'text-gray-300' : 'text-primary-yellow'}
        `}
      >
        Resend {countdown > 0 ? `in ${countdown}s` : ''}
      </button>
    </>
  )
}

const OTPForm = () => {
  const router = useRouter()
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { email, user, otp: storedOtp, phone_number } = useAuthStore()
  const maskedPhoneNumber = maskMobileNumber(phone_number)
  const searchParams = useSearchParams()
  const nextUrl = searchParams.get('nextUrl')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return

    setIsLoading(true)
    try {
      console.log('Submitting OTP: ', otp) // Debug log
      const res = await signIn('credentials', {
        redirect: false,
        email,
        otp,
      })

      if (res?.error) {
        console.log('Sign-in error: ', res.error) // Debug log
        setError('Incorrect OTP')
        setIsLoading(false)
      } else {
        if (nextUrl) {

        //  router.push(nextUrl)
        window.location.href=nextUrl
        }
        else {
          await revalidatePath('/')
          router.prefetch('/')
        }
        toast({
          title: 'Success',
          description: 'Logged in successfully',
          variant: 'success',
        })
        setTimeout(() => {
          if (!nextUrl) router.back()

          setIsLoading(false)
        }, 1000)
      }
    } catch (error) {
      console.error('Failed to verify OTP:', error)
      setError('An error occurred. Please try again.')
      setIsLoading(false)
    }
  }

  // Debugging: Add useEffect to log OTP state changes
  useEffect(() => {
    console.log('Stored OTP: ', storedOtp) // Debug log
    console.log('Entered OTP: ', otp) // Debug log
  }, [storedOtp, otp])

  return (
    <div
      className={`
        
      `}
    >
      <h1
        className={`
          text-center text-2xl font-normal

          lg:text-4xl

          md:text-3xl

          sm:text-2xl
        `}
      >
        OTP Verification
      </h1>
      <p
        className={`
          text-center text-sm font-normal

          md:text-base

          sm:text-sm lg:mt-2 mt-1
        `}
      >
        Enter the One Time Password sent to {maskedPhoneNumber}
      </p>
      {/* {storedOtp && (
        <p
          className={`
            text-center text-sm font-semibold text-red-500

            sm:text-base
          `}
        >
          {storedOtp}
        </p>
      )} */}

      <form className='lg:mt-8 mt-7 flex flex-col gap-4 text-black' onSubmit={handleSubmit}>
        <div className='flex flex-col items-center justify-center'>
          {error && <p className='text-sm font-bold text-red-600'>{error}</p>}
          <OTPInput otp={otp} setOtp={setOtp} />
        </div>

        <div className='lg:mt-6 mt-6 lg:px-40'>
          <Button type='submit' disabled={isLoading} loading={isLoading} label='Proceed' />
        </div>
      </form>

      <span
        className={`
          flex items-center justify-center mt-3 gap-3 lg:text-base text-sm font-normal

         
        `}
      >
        Not Received?
        <ResendOTPButton user={user} />
      </span>
    </div>
  )
}

export default OTPForm
